import React, { useEffect, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import modalActions from '@mm/state/modules/modalsState/actions';
import Input from '../../../../../common/components/Input/Input';
import { ExportOutlined, SyncOutlined } from '@ant-design/icons';
import { apiKeysCRUDActions } from '@mm/state/actions/ApiKeysCRUDActions';
import classNames from 'classnames';
import './ZapierPanel.scss';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { landmineNames } from '@mm/constants/landmine-names';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { Help } from '../../../beta/modules/Integrations/NativeIntegrations/components/KlaviyoIntegrations/KlaviyoHelpLink';
import { Link } from '@mm/components';
import { message, notification } from 'antd';

type Props = {
  caption?: string;
  isSuppression?: boolean;
  className?: string;
};

export const ZapierApiKey = ({ caption, isSuppression, className }: Props) => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const busy = useSelector((state: StoreState) => state.apiKeys.busy);
  const hasZapierFeature = useSelector(combinedFeatureFlagsSelector).zapier;
  const apiKeys = useSelector((state: StoreState) => state.apiKeys.entities);

  const dispatch: Dispatch<StoreState> = useDispatch();

  const [apiKeyInputRef, setApiKeyInputRef] = useState<
    HTMLInputElement | undefined
  >(undefined);
  const [isKeyCreationFailing, setIsKeyCreationFailing] = useState(false);

  const zapierKeys = apiKeys.filter(k => k.connectionType === 'Zapier');
  const apiKey = zapierKeys.find(k => k.active);
  const showUpgradeMessage = !busy && !hasZapierFeature;

  const selectApiKeyText = () => {
    if (showUpgradeMessage) {
      openUpgradeModal();
    }
    setTimeout(() => {
      if (apiKeyInputRef) {
        apiKeyInputRef.setSelectionRange(0, apiKeyInputRef.value.length);
      }
    }, 10);
  };

  const copyIconClassName = classNames('fa-copy', {
    far: true,
  });

  const createApiKey = async () => {
    if (activeFacebookPage) {
      const response = await dispatch(
        apiKeysCRUDActions.create(activeFacebookPage.id, {
          connectionType: 'Zapier',
          name: 'Zapier API Key',
        }),
      );
      return response;
    }
  };

  const regenerateKey = async () => {
    if (!busy && apiKey && activeFacebookPage) {
      confirmRegenerateApiKey(async ok => {
        if (ok) {
          await deleteApiKey();
          await createApiKey();
        }
      });
    }
  };

  const openUpgradeModal = (isNoLongerAvailable = false) => {
    const landmineName = isNoLongerAvailable
      ? `${landmineNames.FEATURE_NO_LONGER_AVAILABLE}_${landmineNames.INTEGRATIONS_ZAPIER}`
      : landmineNames.INTEGRATIONS_ZAPIER;

    dispatch(
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName,
        feature: 'zapier',
      }),
    );
  };

  const deleteApiKey = async () => {
    if (activeFacebookPage && apiKey) {
      await dispatch(apiKeysCRUDActions.delete(activeFacebookPage.id, apiKey));
    }
  };

  const getApiKeys = async () => {
    if (activeFacebookPage) {
      await dispatch(apiKeysCRUDActions.retrieveAll(activeFacebookPage?.id));
    }
  };

  const confirmRegenerateApiKey = (
    onCloseHandler: (okResult: boolean) => void,
  ) => {
    dispatch(
      modalActions.openGeneralConfirmModal({
        headerText: '\u26a0 Regenerate Zapier API Key',
        okButtonText: 'Regenerate Key',
        onCloseHandler,
        messageText: (
          <>
            <p>All Zaps using this API Key will be automatically paused.</p>
            <p>
              The new API Key will need to be added to Zapier to reactivate
              paused Zaps.
            </p>
            <p>This step cannot be undone.</p>
          </>
        ),
      }),
    );
  };

  const onCopy = () => {
    notification.success({
      message: (
        <div className="zapier-panel__message">
          <div style={{ fontWeight: 500 }}>API Key is copied.</div>
          <div>
            <Link
              href="https://zapier.com/apps/mobilemonkey/integrations"
              isBlank
              capitalize={false}
              rel="noopener noreferrer"
              className="zapier-panel__link"
            >
              Go to the Zapier{' '}
            </Link>{' '}
            to access your account
          </div>
        </div>
      ),
    });
  };

  // Create an API key if we don't have one
  useEffect(() => {
    if (
      !busy &&
      apiKeys &&
      !apiKey &&
      !showUpgradeMessage &&
      activeFacebookPage &&
      !isKeyCreationFailing
    ) {
      const createKey = async () => {
        const res = await createApiKey();
        if (res?.error) {
          // Note: We can get more spohisticated at handling the error if needed later.
          // Now it is good enough, being a step forward from never ending loading spinner
          setIsKeyCreationFailing(true);
        }
      };
      createKey();
    }
  }, [busy, apiKeys, activeFacebookPage, isKeyCreationFailing]);

  useEffect(() => {
    if (activeFacebookPage) {
      getApiKeys();
    }
  }, [chatbotId]);

  return (
    <div className={classNames('zapier-panel__field-wrapper', className)}>
      <div className="zapier-panel__field-container">
        <div className="zapier-panel__field-title">
          <div className="zapier-panel__field-title-row">
            <p className="zapier-panel__field-container-name">
              {' '}
              {isSuppression && <span>1.</span>} Copy API Key
            </p>
            <Help
              link="https://customers.ai/help/l/en/article/xz9zrxifz3-zapier"
              text="Help Center"
            />
          </div>

          {caption && (
            <div className="zapier-panel__api-key-caption">{caption}</div>
          )}
        </div>
        <div className="zapier-panel__field">
          <div className="zapier-panel__api-key-row">
            <Input
              inputRef={setApiKeyInputRef}
              className="zapier-panel__api-key chatbot__input"
              readOnly="readonly"
              value={
                showUpgradeMessage
                  ? 'Upgrade to unlock this feature!'
                  : (apiKey && apiKey.authToken) || ''
              }
              onFocus={selectApiKeyText}
            />
            <CopyToClipboard
              text={(apiKey && apiKey.authToken) || ''}
              onCopy={onCopy}
            >
              <PrimaryButton
                className="zapier-panel--header-button"
                color="secondary-blue"
                disabled={showUpgradeMessage}
              >
                <i className={copyIconClassName} />
                Copy Key
              </PrimaryButton>
            </CopyToClipboard>
            <PrimaryButton
              className="zapier-panel--header-button"
              color="secondary-blue"
              onClick={regenerateKey}
              disabled={showUpgradeMessage}
            >
              <SyncOutlined color="#2f54eb" />
              Regenerate Key
            </PrimaryButton>
          </div>
        </div>
      </div>
      {isSuppression && (
        <div className="zapier-panel__field-container-name">
          2. Provide it to the Zapier{' '}
          <Link
            href="https://zapier.com/apps/mobilemonkey/integrations"
            isBlank
            capitalize={false}
            rel="noopener noreferrer"
          >
            <ExportOutlined className="zapier-panel__export-icon" />
          </Link>
        </div>
      )}
    </div>
  );
};
